// The header's account menu (Plan 7): a button with who is signed in opening the account's pages — Alertas, Equipe,
// Cobrança, Consultor (consultorias), Segurança, Status das fontes — and Sair. It also names the customer (account) the
// screens act for — one login belongs to one account (Plan 4a.1; other companies are seen only through what they
// share, in the Carteira). Signed out: Entrar / Criar conta.
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setActiveCustomerId } from '../lib/api.ts';
import { authApi, useAuthMutation } from '../lib/auth.ts';
import { useSession } from '../lib/session.ts';

const ITEM = 'block px-4 py-2 text-sm text-ink no-underline hover:bg-line-2';

export function AccountMenu() {
  const { t } = useTranslation();
  const s = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const signOut = useAuthMutation(() => authApi.signOut());
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  // closes on Escape or a click outside
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  if (s.status === 'loading') return null;
  if (s.status === 'anonymous')
    return (
      <div className="flex items-center gap-3 text-sm">
        <Link to="/entrar" className="font-semibold">
          {t('conta.signIn')}
        </Link>
        <Link to="/cadastro">{t('conta.signUp')}</Link>
      </div>
    );

  const live = s.me.customers.filter((c) => c.status !== 'deleted');
  const current = live.find((c) => c.id === s.customerId);
  const close = () => setOpen(false);
  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="account-menu"
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-10 max-w-64 items-center gap-2 rounded-button border border-line bg-card px-3.5 py-1 text-left text-sm font-semibold text-ink hover:bg-line-2"
      >
        {/* the account the screens act for stays visible, not only inside the menu */}
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate">{s.me.user.name}</span>
          {current && <span className="truncate text-xs font-normal text-body">{current.name}</span>}
        </span>
        <span aria-hidden="true" className="text-faint">
          ▾
        </span>
      </button>
      {open && (
        <nav id="account-menu" aria-label={t('shell.account.menu')} className="absolute top-12 right-0 z-50 w-64 rounded-card border border-line bg-card py-1.5 shadow-md">
          <div className="flex flex-col gap-1 border-b border-line-2 px-4 pt-1.5 pb-2.5">
            <div className="min-w-0">
              <span className="block text-xs text-faint">{t('conta.signedInAs')}</span>
              <span className="block truncate font-semibold text-ink">{s.me.user.name}</span>
            </div>
            {current && (
              <div className="min-w-0">
                <span className="block text-xs text-faint">{t('conta.customer')}</span>
                <span className="block truncate text-sm text-body">{current.name}</span>
              </div>
            )}
          </div>
          <ul className="py-1">
            <li>
              <Link to="/alertas" search={(prev) => ({ titular: prev.titular })} onClick={close} className={ITEM}>
                {t('shell.account.alertas')}
              </Link>
            </li>
            {current && (
              <li>
                <Link to="/equipe" search={(prev) => ({ titular: prev.titular })} onClick={close} className={ITEM}>
                  {t('shell.account.equipe')}
                </Link>
              </li>
            )}
            <li>
              <Link to="/cobranca" search={(prev) => ({ titular: prev.titular })} onClick={close} className={ITEM}>
                {t('shell.account.cobranca')}
              </Link>
            </li>
            {current?.isConsultant && (
              <li>
                <Link to="/parceiro" search={(prev) => ({ titular: prev.titular })} onClick={close} className={ITEM}>
                  {t('shell.account.consultor')}
                </Link>
              </li>
            )}
            <li>
              <Link to="/conta/seguranca" onClick={close} className={ITEM}>
                {t('shell.account.seguranca')}
              </Link>
            </li>
          </ul>
          <ul className="border-t border-line-2 py-1">
            <li>
              <Link to="/status" search={(prev) => ({ titular: prev.titular })} onClick={close} className={ITEM}>
                {t('shell.account.status')}
              </Link>
            </li>
            <li>
              <button
                type="button"
                disabled={signOut.isPending}
                className={`${ITEM} w-full text-left disabled:opacity-50`}
                onClick={() =>
                  signOut.mutate(undefined, {
                    onSettled: () => {
                      setActiveCustomerId(null);
                      qc.clear();
                      setOpen(false);
                      void navigate({ to: '/entrar' });
                    },
                  })
                }
              >
                {t('conta.signOut')}
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}
